/**
 * 后台 - 视频任务记录
 *
 * 管理员可看到所有用户的任务，含完整错误信息（不暴露给用户端）
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Loader2,
  RefreshCw,
  Trash2,
  Eye,
  X,
} from 'lucide-react';
import {
  videoAdminApi,
  type ListAdminVideosParams,
  type VideoJobAdminDetail,
  type VideoJobAdminView,
} from '../services/videoAdminApi';
import type { VideoStatus, VideoMode } from '../services/videoApi';
import toast from '../../../components/Toast';
import { withAuthToken } from '../../../utils/storageUrl';
import { formatBeijingDateTime } from '../../../utils/time';

const PAGE_SIZE = 20;

const STATUS_FILTERS: Array<{ value: VideoStatus | ''; label: string }> = [
  { value: '', label: '全部' },
  { value: 'pending', label: '排队' },
  { value: 'submitting', label: '提交中' },
  { value: 'processing', label: '生成中' },
  { value: 'completed', label: '已完成' },
  { value: 'failed', label: '失败' },
  { value: 'timeout', label: '超时' },
];

const MODE_FILTERS: Array<{ value: VideoMode | ''; label: string }> = [
  { value: '', label: '全部模式' },
  { value: 'text2video', label: '文生视频' },
  { value: 'img2video', label: '图生视频' },
  { value: 'first_last_frame', label: '首尾帧' },
];

const SOURCE_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: '全部来源' },
  { value: 'web', label: 'Web' },
  { value: 'api', label: 'API' },
  { value: 'director', label: '导演模式' },
];

export default function VideoJobsTab() {
  const [items, setItems] = useState<VideoJobAdminView[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState<VideoStatus | ''>('');
  const [modeFilter, setModeFilter] = useState<VideoMode | ''>('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [search, setSearch] = useState('');

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<VideoJobAdminDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const params: ListAdminVideosParams = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      status: statusFilter || undefined,
      mode: modeFilter || undefined,
      source: sourceFilter || undefined,
      userEmail: userEmail.trim() || undefined,
      search: search.trim() || undefined,
    }),
    [page, statusFilter, modeFilter, sourceFilter, userEmail, search]
  );

  const load = async () => {
    setLoading(true);
    try {
      const res = await videoAdminApi.list(params);
      setItems(res.data);
      setTotal(res.pagination.total);
      setTotalPages(res.pagination.totalPages);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载视频任务失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, modeFilter, sourceFilter]);

  const handleSearch = () => {
    setPage(1);
    load();
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`确定删除选中的 ${selectedIds.size} 个任务？软删除：仅隐藏记录，文件保留，可恢复。`)) return;
    try {
      const res = await videoAdminApi.deleteMany(Array.from(selectedIds));
      toast.success(`已隐藏 ${res.hiddenCount} 条（文件保留）`);
      setSelectedIds(new Set());
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败');
    }
  };

  const handleViewDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const data = await videoAdminApi.detail(id);
      setDetail(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '获取详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-light-text">视频任务记录</h2>
        <p className="text-sm text-light-muted mt-1">共 {total} 条任务</p>
      </div>

      {/* 筛选栏 */}
      <div className="bg-white border border-light-border rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_FILTERS.map((opt) => (
            <button
              key={opt.value || 'all'}
              onClick={() => {
                setStatusFilter(opt.value);
                setPage(1);
              }}
              className={`px-3 py-1 rounded-full text-xs ${
                statusFilter === opt.value
                  ? 'bg-accent-blue text-white'
                  : 'bg-light-hover text-light-secondary'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {MODE_FILTERS.map((opt) => (
            <button
              key={opt.value || 'all'}
              onClick={() => {
                setModeFilter(opt.value);
                setPage(1);
              }}
              className={`px-3 py-1 rounded-full text-xs ${
                modeFilter === opt.value
                  ? 'bg-accent-blue text-white'
                  : 'bg-light-hover text-light-secondary'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {SOURCE_FILTERS.map((opt) => (
            <button
              key={opt.value || 'all'}
              onClick={() => {
                setSourceFilter(opt.value);
                setPage(1);
              }}
              className={`px-3 py-1 rounded-full text-xs ${
                sourceFilter === opt.value
                  ? 'bg-accent-blue text-white'
                  : 'bg-light-hover text-light-secondary'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            placeholder="按邮箱筛选"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="input-field flex-1 min-w-0"
          />
          <input
            type="text"
            placeholder="按提示词搜索"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="input-field flex-1 min-w-0"
          />
          <button onClick={handleSearch} className="btn-secondary sm:shrink-0">
            搜索
          </button>
          <button onClick={load} className="btn-secondary p-2 sm:shrink-0" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* 批量操作栏 */}
      {selectedIds.size > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl px-4 py-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-rose-700">已选中 {selectedIds.size} 条</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-light-secondary hover:underline"
            >
              取消
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              删除（软）
            </button>
          </div>
        </div>
      )}

      {/* 表格 */}
      <div className="bg-white border border-light-border rounded-2xl overflow-hidden">
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-light-border text-sm">
            <thead className="bg-light-hover">
              <tr className="text-left text-xs uppercase tracking-wider text-light-muted">
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && selectedIds.size === items.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="px-3 py-3">用户</th>
                <th className="px-3 py-3">Prompt</th>
                <th className="px-3 py-3">来源</th>
                <th className="px-3 py-3">模型</th>
                <th className="px-3 py-3">模式</th>
                <th className="px-3 py-3">参数</th>
                <th className="px-3 py-3">状态</th>
                <th className="px-3 py-3">积分</th>
                <th className="px-3 py-3">创建时间</th>
                <th className="px-3 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-light-border">
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-12 text-center text-light-muted">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-12 text-center text-light-muted">
                    没有任务
                  </td>
                </tr>
              ) : (
                items.map((job) => (
                  <tr key={job.id} className="hover:bg-light-hover/50">
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(job.id)}
                        onChange={() => handleToggleSelect(job.id)}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="text-sm">{job.userEmail || '-'}</div>
                      {job.userName && (
                        <div className="text-xs text-light-muted">{job.userName}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 max-w-[260px]">
                      <div className="line-clamp-2 text-light-text" title={job.prompt}>
                        {job.prompt}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <SourceBadge source={job.source} />
                    </td>
                    <td className="px-3 py-2.5">
                      <code className="text-xs text-violet-700 break-all">{job.model}</code>
                    </td>
                    <td className="px-3 py-2.5 text-xs">{getModeLabel(job.mode)}</td>
                    <td className="px-3 py-2.5 text-xs text-light-muted">
                      {job.duration}s · {job.resolution} · {job.aspectRatio}
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="px-3 py-2.5">{job.creditsUsed}</td>
                    <td className="px-3 py-2.5 text-xs text-light-muted whitespace-nowrap">
                      {formatDateTime(job.createdAt)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        onClick={() => handleViewDetail(job.id)}
                        className="p-1.5 rounded-lg hover:bg-light-hover text-light-secondary"
                        title="详情"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="divide-y divide-light-border lg:hidden">
          {loading && items.length === 0 ? (
            <div className="py-12 text-center text-light-muted">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-light-muted">没有任务</div>
          ) : (
            items.map((job) => (
              <div key={job.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <label className="flex items-start gap-3 min-w-0 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(job.id)}
                      onChange={() => handleToggleSelect(job.id)}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-light-text break-all">
                        {job.userEmail || '-'}
                      </div>
                      <div className="mt-1 line-clamp-3 text-sm text-light-secondary">
                        {job.prompt}
                      </div>
                    </div>
                  </label>
                  <button
                    onClick={() => handleViewDetail(job.id)}
                    className="shrink-0 p-2 rounded-lg hover:bg-light-hover text-light-secondary"
                    title="详情"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
                {job.userName && <div className="text-xs text-light-muted">昵称：{job.userName}</div>}
                <div className="grid grid-cols-2 gap-2">
                  <InfoTile label="来源" value={<SourceBadge source={job.source} />} />
                  <InfoTile label="模型" value={<code className="text-xs text-violet-700">{job.model}</code>} />
                  <InfoTile label="模式" value={getModeLabel(job.mode)} />
                  <InfoTile label="状态" value={<StatusBadge status={job.status} />} />
                  <InfoTile label="参数" value={`${job.duration}s · ${job.resolution}`} />
                  <InfoTile label="比例 / 积分" value={`${job.aspectRatio} · ${job.creditsUsed}`} />
                </div>
                <div className="text-xs text-light-muted">创建时间：{formatDateTime(job.createdAt)}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-1.5 rounded-lg border border-light-border hover:bg-light-hover disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-light-muted">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="p-1.5 rounded-lg border border-light-border hover:bg-light-hover disabled:opacity-40"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 详情弹窗 */}
      {detail && (
        <DetailModal
          detail={detail}
          onClose={() => setDetail(null)}
          loading={detailLoading}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: VideoStatus }) {
  const map: Record<VideoStatus, { label: string; cls: string }> = {
    pending: { label: '排队中', cls: 'bg-blue-100 text-blue-700' },
    submitting: { label: '提交中', cls: 'bg-blue-100 text-blue-700' },
    processing: { label: '生成中', cls: 'bg-amber-100 text-amber-700' },
    completed: { label: '已完成', cls: 'bg-emerald-100 text-emerald-700' },
    failed: { label: '失败', cls: 'bg-rose-100 text-rose-700' },
    timeout: { label: '超时', cls: 'bg-zinc-100 text-zinc-700' },
  };
  const c = map[status] || { label: status, cls: 'bg-light-hover text-light-secondary' };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.cls}`}>{c.label}</span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    web: { label: 'Web', cls: 'bg-slate-100 text-slate-700' },
    api: { label: 'API', cls: 'bg-violet-100 text-violet-700' },
    director: { label: '导演模式', cls: 'bg-emerald-100 text-emerald-700' },
  };
  const c = map[source] || { label: source || '-', cls: 'bg-light-hover text-light-secondary' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.cls}`}>{c.label}</span>;
}

function getModeLabel(mode: VideoMode): string {
  switch (mode) {
    case 'text2video':
      return '文生视频';
    case 'img2video':
      return '图生视频';
    case 'first_last_frame':
      return '首尾帧';
    default:
      return mode;
  }
}

function formatDateTime(value: string): string {
  return formatBeijingDateTime(value);
}

function DetailModal({
  detail,
  onClose,
  loading,
}: {
  detail: VideoJobAdminDetail;
  onClose: () => void;
  loading: boolean;
}) {
  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(detail.prompt || '');
      toast.success('Prompt 已复制');
    } catch {
      toast.error('复制 Prompt 失败');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12 px-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
          <h3 className="font-semibold text-light-text">任务详情</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-light-hover">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          ) : (
            <>
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="font-medium text-light-text">Prompt</div>
                  <button
                    onClick={handleCopyPrompt}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-light-border px-2.5 py-1 text-xs text-light-secondary hover:bg-light-hover"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    复制
                  </button>
                </div>
                <pre className="rounded-xl bg-light-hover p-3 text-xs whitespace-pre-wrap break-all text-light-text">
                  {detail.prompt || '-'}
                </pre>
              </div>

              {(detail.referenceImages.length > 0 || detail.firstFrameImage || detail.lastFrameImage) && (
                <div className="space-y-3">
                  <div className="font-medium text-light-text">输入参考图</div>
                  {detail.referenceImages.length > 0 && (
                    <InputImageGrid label="参考图" images={detail.referenceImages} />
                  )}
                  {detail.firstFrameImage && (
                    <InputImageGrid label="首帧" images={[detail.firstFrameImage]} />
                  )}
                  {detail.lastFrameImage && (
                    <InputImageGrid label="尾帧" images={[detail.lastFrameImage]} />
                  )}
                </div>
              )}

              {detail.resultUrl && (
                <div>
                  <div className="font-medium mb-1">视频</div>
                  <video
                    src={withAuthToken(detail.resultUrl)}
                    poster={withAuthToken(detail.coverUrl) || undefined}
                    controls
                    className="w-full rounded-xl bg-black"
                  />
                </div>
              )}

              <KV label="ID" value={detail.id} mono />
              <KV label="用户" value={`${detail.userEmail || '-'} (${detail.userId})`} />
              <KV label="状态" value={<StatusBadge status={detail.status} />} />
              <KV label="来源" value={<SourceBadge source={detail.source} />} />
              <KV label="模式" value={getModeLabel(detail.mode)} />
              <KV label="模型" value={detail.model} />
              <KV
                label="参数"
                value={`${detail.duration}s · ${detail.resolution} · ${detail.aspectRatio}${
                  detail.cameraMovement ? ' · ' + detail.cameraMovement : ''
                }${detail.motionIntensity ? ' · ' + detail.motionIntensity : ''}${
                  detail.enableAudio ? ' · 含音频' : ''
                }`}
              />
              <KV label="参考图数" value={detail.referenceImageCount} />
              <KV label="首/尾帧" value={`${detail.hasFirstFrame ? '✓' : '×'} / ${detail.hasLastFrame ? '✓' : '×'}`} />
              <KV label="渠道" value={detail.channelId || '-'} mono />
              <KV label="上游 task_id" value={detail.upstreamTaskId || '-'} mono />
              <KV label="积分" value={detail.creditsUsed} />
              <KV label="存储" value={`${detail.resultStorageType || '-'} / ${detail.resultStorageKey || '-'}`} mono />
              <KV
                label="时间线"
                value={
                  <div className="space-y-1 text-xs text-light-muted">
                    <div>创建：{formatBeijingDateTime(detail.createdAt)}</div>
                    {detail.startedAt && (
                      <div>开始：{formatBeijingDateTime(detail.startedAt)}</div>
                    )}
                    {detail.completedAt && (
                      <div>完成：{formatBeijingDateTime(detail.completedAt)}</div>
                    )}
                  </div>
                }
              />

              {detail.error && (
                <div>
                  {detail.userFacingErrorMessage && (
                    <div className="mb-3">
                      <div className="font-medium text-amber-600 mb-1">用户侧最终报错</div>
                      <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-sm text-amber-800">
                        {detail.userFacingErrorMessage}
                      </div>
                    </div>
                  )}
                  <div className="font-medium text-rose-600 mb-1">错误信息（仅管理员可见）</div>
                  <pre className="bg-rose-50 border border-rose-100 rounded-lg p-3 text-xs whitespace-pre-wrap font-mono text-rose-800 overflow-x-auto">
                    {JSON.stringify(detail.error, null, 2)}
                  </pre>
                </div>
              )}

              {detail.upstreamRawResult ? (
                <div>
                  <div className="font-medium mb-1">上游原始返回</div>
                  <pre className="bg-light-hover rounded-lg p-3 text-xs whitespace-pre-wrap font-mono text-light-secondary overflow-x-auto">
                    {JSON.stringify(detail.upstreamRawResult, null, 2)}
                  </pre>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3">
      <div className="text-light-muted text-xs">{label}</div>
      <div className={mono ? 'font-mono text-xs' : ''}>{value}</div>
    </div>
  );
}

function InfoTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className="mt-1 text-sm text-light-text">{value}</div>
    </div>
  );
}

function InputImageGrid({ label, images }: { label: string; images: string[] }) {
  return (
    <div>
      <div className="mb-2 text-xs text-light-muted">{label}</div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {images.map((image, index) => (
          <a
            key={`${label}-${index}-${image}`}
            href={withAuthToken(image)}
            target="_blank"
            rel="noreferrer"
            className="group block overflow-hidden rounded-xl border border-light-border bg-light-hover"
          >
            <img
              src={withAuthToken(image)}
              alt={`${label}${index + 1}`}
              className="h-32 w-full object-cover transition-transform group-hover:scale-[1.02]"
            />
          </a>
        ))}
      </div>
    </div>
  );
}
